import { createContext, type JSX, useContext } from 'solid-js'
import { useAuth as useClerkAuth, useClerk } from './clerk'
import { AppRoutes } from '../app-routes'

export interface AuthContextType {
  readonly isAuthenticated: () => boolean
  readonly isLoading: () => boolean
  readonly getToken: () => Promise<string | null>
  readonly signOut: () => Promise<void>
  readonly signIn: () => void
}

const AuthContext = createContext<AuthContextType>()

export const AuthProvider = (props: { children: JSX.Element }): JSX.Element => {
  const clerkAuth = useClerkAuth()
  const clerk = useClerk()

  const value: AuthContextType = {
    isAuthenticated: () => clerkAuth.isSignedIn() ?? false,
    isLoading: () => !clerkAuth.isLoaded(),
    getToken: async () => {
      return (await clerkAuth.getToken()) ?? null
    },
    signOut: async () => {
      await clerk().signOut({ redirectUrl: window.location.origin })
    },
    signIn: () => {
      window.location.assign(AppRoutes.Login())
    }
  }

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const getClerkToken = async (): Promise<string | null> => {
  // This function can be used in places where hooks can't be used (like axios interceptor)
  // It will be called after Clerk is initialized.
  // Since Clerk Core 3, getToken throws ClerkOfflineError when offline instead of returning null,
  // so callers can tell "offline" from "signed out". Let it propagate; check it with isClerkOfflineError.
  const token = await window.Clerk?.session?.getToken()
  return token ?? null
}
